/**
 * The fixture's date line (SE-6975, SE-6976).
 *
 * **Three precisions, three different sentences.** A fixture six weeks out
 * routinely has no kick-off time and sometimes no exact date; that is the
 * normal state of a fixture list, not missing data, and the spike behind
 * MR-0.1 is explicit that pairings publish long before times and venues do.
 * Rendering an unknown as a confident value is the failure mode worth
 * preventing: "00:00" reads as a midnight kick-off, and collapsing a two-day
 * window to its first day sends someone on the wrong date.
 *
 * Formatting is done by hand from the civil parts rather than through
 * `toLocaleDateString`, which resolves against the machine's timezone and would
 * shift the date for every viewer west of the venue.
 * @module features/fixture-planning/utils/toDateLabel
 */

import { fromIso } from "./monthGrid";

import type { FixtureDatePrecision } from "./toFixtureRows";

/** Month names, indexed 1–12 with a dead slot so the maths reads plainly. */
const MONTHS = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Renders one civil date as "18 Aug 2026".
 * @param iso - `YYYY-MM-DD`.
 * @returns The rendered date, or the raw string when it is not a civil date.
 */
export const formatCivilDate = (iso: string): string => {
  const parsed = fromIso(iso);
  // Falling back to the raw value rather than to a placeholder: if the server
  // ever sends a shape this does not know, showing it is how anyone finds out.
  if (parsed === null) return iso;

  return `${parsed.day} ${MONTHS[parsed.month]} ${parsed.year}`;
};

/** What the date line needs. */
export interface DateLabelInput {
  /** First civil date, `YYYY-MM-DD`. */
  readonly date: string;
  /** Last civil date of a window, or null. */
  readonly endDate: string | null;
  /** How exact `date` is. */
  readonly datePrecision: FixtureDatePrecision;
  /** Civil kick-off `HH:mm`, or null when unconfirmed. */
  readonly startTime: string | null;
}

/**
 * Builds the fixture's date line.
 * @param input - The fixture's dates.
 * @returns One line a reader can trust.
 */
export const toDateLabel = (input: DateLabelInput): string => {
  if (input.datePrecision === "ROUND_ONLY") {
    // No day at all is known yet. Naming the month is the most that is true,
    // and it is more useful than an invented date.
    const parsed = fromIso(input.date);

    return parsed === null
      ? "Date TBC"
      : `${MONTHS[parsed.month]} ${parsed.year} • date TBC`;
  }

  if (input.datePrecision === "WINDOW") {
    // A window with no end is a window of one day as far as anyone knows, so
    // it renders as that day rather than as a range with a missing half.
    return input.endDate === null
      ? formatCivilDate(input.date)
      : `${formatCivilDate(input.date)} – ${formatCivilDate(input.endDate)}`;
  }

  return input.startTime === null
    ? `${formatCivilDate(input.date)} • time TBC`
    : `${formatCivilDate(input.date)} • ${input.startTime}`;
};
